import { computed } from 'vue';
import { useStore } from 'vuex';
import UtdCache from '_acaSrc/utility/UtdCache';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import { trackAndNavigateOpts } from '_acaSrc/plugins/UtdEventManager';
import { getWindow } from '_acaSrc/utility/DOM';

export default function useExpertAiAction() {
    const store = useStore();
    const isAnonymousUser = computed(() => store.getters['app/isAnonymousUser']);
    const utdAIURL = computed(() => store.getters['app/utdAIURL']);
    const isAnonymousExpertClinicianAccessEnabled = computed(() =>
        store.getters['feature/isAnonymousExpertClinicianAccessEnabled']);

    const createUtdAiRedirectUrl = ({
        queryParam = 'search',
        queryValue = ''
    } = {}) => {
        if (!utdAIURL.value) {
            console.warn('UpToDate AI URL not available.');
            return;
        }
        if (!queryValue) {
            return utdAIURL.value;
        }
        const params = new URLSearchParams();
        params.append(queryParam, queryValue);
        return `${utdAIURL.value}#${params.toString()}`;
    };

    const resolveQueryData = ({
        queryParam = 'search',
        searchTerm = '',
        prompt = ''
    } = {}) => {
        const selectedQueryParam = queryParam === 'prompt' ? 'prompt' : 'search';
        const selectedQueryValue = selectedQueryParam === 'prompt' ? prompt : searchTerm;
        return { selectedQueryParam, selectedQueryValue };
    };

    const getTrackingData = ({ selectedQueryParam, selectedQueryValue }) => {
        return selectedQueryParam === 'prompt'
            ? { prompt: selectedQueryValue }
            : { searchTerm: selectedQueryValue };
    };

    const shouldUseAnonymousFlow = () =>
        isAnonymousUser.value && !isAnonymousExpertClinicianAccessEnabled.value;

    const openWindow = ({ redirectUrl }) => {
        window.open(redirectUrl, '_self');
    };

    const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);
    const router = computed(() => store.getters['app/router']);

    const navigateToLogin = () => {
        const relativeUrl = getWindow().location.pathname + getWindow().location.search;
        new UtdCache().setPersistent('ssoRedirectURL', relativeUrl, 1);
        if (isAuthenticatedByIp.value) {
            router.value.go('login');
        }
        else {
            getWindow().location.href = '/login';
        }
    };

    const handleAnonymousFlow = ({
        redirectUrl,
        uiElementName,
        trackingData
    }) => {
        logUiClickEventHelper({ targetUrl: null, uiElementName, ...trackingData });
        const cache = new UtdCache();
        cache.setPersistent('isExpertAiRedirect', true, 1);
        cache.setPersistent('expertAiRedirectUrl', redirectUrl, 1);
        navigateToLogin();
    };

    const handleTrackedNavigation = async({
        redirectUrl,
        uiElementName,
        trackingData
    }) => {
        await trackAndNavigateOpts(utdAIURL.value, uiElementName, trackingData);
        openWindow({ redirectUrl });
    };

    const handleExpertAiAction = async({
        event,
        searchTerm = '',
        prompt = '',
        queryParam = 'search',
        uiElementName }) => {
        const queryData = resolveQueryData({ queryParam, searchTerm, prompt });
        const redirectUrl = createUtdAiRedirectUrl({
            queryParam: queryData.selectedQueryParam,
            queryValue: queryData.selectedQueryValue
        });
        if (!redirectUrl) {
            return;
        }
        const trackingData = getTrackingData(queryData);

        event.preventDefault();
        event.stopPropagation();

        if (shouldUseAnonymousFlow()) {
            handleAnonymousFlow({
                redirectUrl,
                uiElementName,
                trackingData
            });
            return;
        }

        await handleTrackedNavigation({
            redirectUrl,
            uiElementName,
            trackingData
        });
    };

    return {
        handleExpertAiAction
    };
}

